import React, { ReactElement } from 'react';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import { useGetLocationBreakdown, OverviewLocationFilters, LocationBreakdownRow } from '@Apis/companyDashboard';
import { BreakdownView } from './breakdownSelection';
import LocationBreakdownTable from './LocationBreakdownTable';
import './LocationBreakdownPanel.scss';

export type { BreakdownView };

export const DEFAULT_BREAKDOWN_VIEW: BreakdownView = 'discipline';

// One entry per switcher button. `noun`/`singular` are the words used in the panel subtext
// ("Showing 5 Disciplines") and the empty state, so the wording can never drift from the
// selected button. `id` doubles as the endpoint's `groupBy` value.
export const BREAKDOWN_VIEWS: { id: BreakdownView; label: string; noun: string; singular: string }[] = [
    { id: 'department', label: 'By Department', noun: 'Departments', singular: 'Department' },
    { id: 'discipline', label: 'By Discipline', noun: 'Disciplines', singular: 'Discipline' },
    { id: 'phase', label: 'By Phase', noun: 'Phases', singular: 'Phase' },
    { id: 'tag', label: 'By Tag', noun: 'Tags', singular: 'Tag' },
];

interface LocationBreakdownPanelProps {
    locationName: string;
    locationKey: string;
    view: BreakdownView;
    onViewChange: (view: BreakdownView) => void;
    onClose: () => void;
    isExpanded: boolean;
    /** The page's active filters, so the panel counts the tasks its location row counts. */
    filters: OverviewLocationFilters;
    /** Drills into one group's tasks in the sidebar. */
    onGroupSelect: (row: LocationBreakdownRow) => void;
}

const LocationBreakdownPanel = ({
    locationName,
    locationKey,
    view,
    onViewChange,
    onClose,
    isExpanded,
    filters,
    onGroupSelect,
}: LocationBreakdownPanelProps): ReactElement => {

    const activeView = BREAKDOWN_VIEWS.find((candidate) => candidate.id === view) ?? BREAKDOWN_VIEWS[0];

    // Gated on `isExpanded`, not on being mounted: the panel stays mounted for the length of
    // the collapse animation, and a row on its way out should not start a fetch. Switching the
    // view switcher changes the query key, so the table re-fetches and re-renders on its own.
    const { data, isLoading, error } = useGetLocationBreakdown(locationKey, view, filters, isExpanded);

    // `data !== undefined` — not `!isLoading` — is what says "a response has landed".
    // Collapsing a row mid-flight disables the query, and a query disabled before it ever
    // resolved reports `isLoading: false` with no data and no error; gating the empty state on
    // that would tell the user this location has no breakdown without ever having asked.
    const hasLoaded = data !== undefined;
    const rows = data?.groups ?? [];
    const hasRows = rows.length > 0;
    const hasCounts = hasLoaded && !error;

    return (
        <div className='location-breakdown-panel' aria-hidden={!isExpanded}>

            <div className='lbp-header'>

                <div className='lbp-header__text'>
                    <div className='lbp-title'>Breakdown for {locationName}</div>
                    <div className='lbp-subtitle'>
                        Showing {hasCounts ? rows.length : '—'}{' '}
                        {hasCounts && rows.length === 1 ? activeView.singular : activeView.noun}
                    </div>
                </div>

                <div className='lbp-header__actions'>

                    <div className='lbp-switcher' role='group' aria-label='Breakdown view'>
                        {BREAKDOWN_VIEWS.map((candidate) => (
                            <button
                                key={candidate.id}
                                type='button'
                                className={`lbp-switcher__btn${candidate.id === view ? ' lbp-switcher__btn--active' : ''}`}
                                aria-pressed={candidate.id === view}
                                tabIndex={isExpanded ? undefined : -1}
                                onClick={() => onViewChange(candidate.id)}>
                                {candidate.label}
                            </button>
                        ))}
                    </div>

                    <button
                        type='button'
                        className='lbp-close'
                        aria-label='Close breakdown'
                        title='Close breakdown'
                        tabIndex={isExpanded ? undefined : -1}
                        onClick={onClose}>
                        {/* Solid, not regular: the bundled regular-400 subset has no xmark
                            and renders a tofu box. Matches the location detail drawer's
                            close button, which is already `fas fa-xmark`. */}
                        <i className='fa-solid fa-xmark' />
                    </button>

                </div>

            </div>

            {error && (
                <div className='lbp-message lbp-message--error'>
                    Unable to load the breakdown for this location.
                </div>
            )}

            {!error && (
                <LoadingIndicator isLoading={isLoading} hideWhileLoading>
                    {hasRows && (
                        <LocationBreakdownTable
                            key={view}
                            rows={rows}
                            groupLabel={activeView.singular}
                            onGroupSelect={onGroupSelect}
                        />
                    )}
                    {hasLoaded && !hasRows && (
                        <div className='lbp-message'>
                            No {activeView.singular} data available for this location
                        </div>
                    )}
                </LoadingIndicator>
            )}

        </div>
    );
};

export default LocationBreakdownPanel;
